import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, Button, useToast } from '@/components/common';
import { useAuthStore } from '@/store';
import { apiClient } from '@/services/api';
import './billing-section.css';

export function BillingSection() {
  const toast = useToast();
  const user = useAuthStore((s) => s.user);
  const [isLoading, setIsLoading] = useState(false);

  if (!user) return null;

  const isPro = user.tier === 'pro';

  const handleManageSubscription = async () => {
    setIsLoading(true);
    try {
      const response = await apiClient.post<{ url: string }>('/api/v1/billing/portal');
      const url = response.data?.url;
      if (url) {
        window.location.href = url;
      } else {
        toast.error('Could not open billing portal. Please try again.');
      }
    } catch (error) {
      console.error('Billing portal error:', error);
      toast.error('Could not open billing portal. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateStr: string | null | undefined): string => {
    if (!dateStr) return '';
    try {
      return new Date(dateStr).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <Card className="mb-4 sm:mb-6">
      <div className="p-4 sm:p-6">
        <h2 className="billing-section__title">Subscription</h2>

        <div className="billing-section__row">
          <div className="billing-section__info">
            {isPro ? (
              <>
                <div className="billing-section__plan-row">
                  <span className="billing-section__plan-name">Pro Plan</span>
                  <span
                    className={`billing-section__status-badge billing-section__status-badge--${
                      user.subscriptionStatus === 'past_due' ? 'past-due' : 'active'
                    }`}
                  >
                    {user.subscriptionStatus === 'past_due' ? 'Past Due' : 'Active'}
                  </span>
                </div>
                {user.subscriptionPeriodEnd && (
                  <p className="billing-section__renew-text">
                    Subscription renews on {formatDate(user.subscriptionPeriodEnd)}
                  </p>
                )}
              </>
            ) : (
              <div className="billing-section__plan-row">
                <span className="billing-section__plan-name">Free Plan</span>
              </div>
            )}
          </div>

          <div className="billing-section__actions">
            {isPro ? (
              <Button
                variant="secondary"
                size="sm"
                onClick={handleManageSubscription}
                isLoading={isLoading}
                disabled={isLoading}
              >
                Manage Subscription
              </Button>
            ) : (
              <Link to="/pricing">
                <Button variant="primary" size="sm">
                  Upgrade to Pro
                </Button>
              </Link>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
}
